import { Card, CardContent, Typography, Button, Box, Collapse } from '@mui/material';
import { useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useAppStore from '../store/useAppStore';
import api from 'services/api';

export default function CreateProjectCard() {
  const setter = useAppStore((state) => state.setter);
  const newProject = useAppStore((state) => state.newProject);
  const setNewProjectQuotationFile = useAppStore(
    (state) => state.setNewProjectQuotationFile,
  );
  const setNewProjectConfigFile = useAppStore(
    (state) => state.setNewProjectConfigFile,
  );
  // 0226新增
  const setNewProjectUserAudioFiles = useAppStore((state) => state.setNewProjectUserAudioFiles);
  const setNewProjectEngineerAudioFiles = useAppStore((state) => state.setNewProjectEngineerAudioFiles);
  const [showAudioUpload, setShowAudioUpload] = useState(false);
  const userAudioRef = useRef(null);
  const engineerAudioRef = useRef(null);
  // 0226新增

  const showNotification = useAppStore((state) => state.showNotification);
  const setInitEditData = useAppStore((state) => state.setInitEditData);

  const [extractKeyInfo, { isLoading }] =
    api.endpoints.extractKeyInfo.useMutation();

  // 1028 新增：規格書輔助資料（手臂配置點 / IO點資料表），選填
  const [uploadSpecInput] = api.endpoints.uploadSpecInput.useMutation();
  const [pointListFile, setPointListFile] = useState(null);
  const [ioInputFile, setIoInputFile] = useState(null);
  const SPEC_INPUT_ACCEPT = '.xlsx,.xls,.csv,.pdf,.txt,.md,.json';
  const SPEC_INPUT_EXTS = ['xlsx', 'xls', 'csv', 'pdf', 'txt', 'md', 'json'];

  const handleSpecInputChange = (setFile) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 讓同檔可重選
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!SPEC_INPUT_EXTS.includes(ext)) {
      showNotification('請上傳 xlsx / xls / csv / pdf / txt / md / json 檔案', 'error');
      return;
    }
    setFile(file);
  };

  // 專案建立後，把選好的規格書輔助資料上傳到 upload_file/<comp_name>/<project_id>/spec_input/
  const uploadSpecInputs = async (project_id) => {
    const user = useAppStore.getState().user;
    const items = [
      { file: pointListFile, fileType: 'point_list', label: '手臂配置點' },
      { file: ioInputFile, fileType: 'io_input', label: 'IO點資料表' },
    ].filter((item) => item.file);

    for (const { file, fileType, label } of items) {
      const formData = new FormData();
      formData.append('user_id', user.id || '');
      formData.append('comp_name', user.company || '');
      formData.append('project_id', project_id);
      formData.append('file_type', fileType);
      formData.append('file', file);
      try {
        await uploadSpecInput(formData).unwrap();
      } catch (error) {
        console.error(`${label}上傳失敗:`, error);
        showNotification(error?.data?.message || `${label}上傳失敗`, 'error');
      }
    }
    setPointListFile(null);
    setIoInputFile(null);
  };

  const quotationFileRef = useRef();
  const configFileRef = useRef();

  // 0226新增
  const handleUserAudioChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // 可選：限制音訊格式（比較保險）
    const okTypes = new Set([
      'audio/mpeg',
      'audio/wav',
      'audio/x-wav',
      'audio/mp4',
      'audio/webm',
      'audio/ogg',
      'audio/x-m4a',
      'video/mp4',
      'video/webm',
      'video/ogg',
      'video/quicktime'
    ]);
    const bad = files.find((f) => f.type && !okTypes.has(f.type));
    if (bad) {
      showNotification('請上傳音訊檔（mp3 / wav / m4a / webm / ogg）', 'error');
      e.target.value = ''; // 讓同檔可重選
      return;
    }

    setNewProjectUserAudioFiles(files);
    e.target.value = ''; // 讓同檔可重選
  };

  const handleEngineerAudioChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const okTypes = new Set([
      'audio/mpeg',
      'audio/wav',
      'audio/x-wav',
      'audio/mp4',
      'audio/webm',
      'audio/ogg',
      'audio/x-m4a',
      'video/mp4',
      'video/webm',
      'video/ogg',
      'video/quicktime'
    ]);

    const bad = files.find((f) => f.type && !okTypes.has(f.type));
    if (bad) {
      showNotification('請上傳音訊檔（mp3 / wav / m4a / webm / ogg）', 'error');
      e.target.value = '';
      return;
    }

    setNewProjectEngineerAudioFiles(files);
    e.target.value = '';
  };
  // 0226新增

  const handleQuotationFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setNewProjectQuotationFile(file);
    } else {
      showNotification('請選擇 PDF 檔案', 'error');
    }
  };

  const handleConfigFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'image/png') {
      setNewProjectConfigFile(file);
    } else {
      showNotification('請選擇 PNG 圖片檔案', 'error');
    }
  };

  const handleCreateProject = async () => {
    // 驗證輸入
    if (!newProject.name.trim()) {
      showNotification('請輸入專案名稱', 'error');
      return;
    }
    if (!newProject.quotationFile) {
      showNotification('請上傳報價單檔案', 'error');
      return;
    }
    if (!newProject.configFile) {
      showNotification('請上傳配置圖檔案', 'error');
      return;
    }
    const project_id = uuidv4();
    const formData = new FormData();
    formData.append('user_id', useAppStore.getState().user.id);
    formData.append('comp_name', useAppStore.getState().user.company);
    formData.append('project_id', project_id);
    if (newProject.quotationFile) {
      formData.append('file', newProject.quotationFile);
    }
    if (newProject.configFile) {
      formData.append('file', newProject.configFile);
    }

    // 0226新增
    // User 語音（多檔）
    if (newProject.userAudioFiles?.length) {
      newProject.userAudioFiles.forEach((f) => {
        formData.append('file', f); // ✅ 同一個 key 重複 append
        // formData.append('user_audio', f); // ✅ 同一個 key 重複 append
      });
    }

    // Engineer 語音（多檔）
    if (newProject.engineerAudioFiles?.length) {
      newProject.engineerAudioFiles.forEach((f) => {
        formData.append('file', f); // ✅ 同一個 key 重複 append
        // formData.append('engineer_audio', f); // ✅ 同一個 key 重複 append
      });
    }
    // 0226新增

    extractKeyInfo(formData)
      .unwrap()
      .then(async (data) => {
        await uploadSpecInputs(project_id); // 1028 新增
        setInitEditData(Object.values(data)[0]);
        setter('selectedProject', (draft) => {
          draft.project_id = project_id;
          draft.project_name = newProject.name.trim();
          draft.files = [newProject.quotationFile, newProject.configFile];
          draft.ori_project_id = '';
          draft.full_script_id = uuidv4();
        });
      })
      .catch((error) => {
        console.error(error);
        showNotification(error.message || '發生錯誤', 'error');
      });

    return;
  };

  return (
    <Card>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* 檔案上傳區域 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* 報價單上傳 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button variant="outlined" component="label" fullWidth>
              上傳報價單 (PDF)
              <input
                ref={quotationFileRef}
                type="file"
                accept=".pdf"
                hidden
                onChange={handleQuotationFileChange}
              />
            </Button>
            {newProject.quotationFile && (
              <Typography variant="body2" color="success.main" sx={{ pl: 1 }}>
                ✓ {newProject.quotationFile.name}
              </Typography>
            )}
          </Box>

          {/* 配置圖上傳 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button variant="outlined" component="label" fullWidth>
              上傳配置圖 (PNG)
              <input
                ref={configFileRef}
                type="file"
                accept=".png"
                hidden
                onChange={handleConfigFileChange}
              />
            </Button>
            {newProject.configFile && (
              <Typography variant="body2" color="success.main" sx={{ pl: 1 }}>
                ✓ {newProject.configFile.name}
              </Typography>
            )}
          </Box>
        </Box>

        {/* 1028 新增：手臂配置點 / IO點資料表（選填） */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button variant="outlined" component="label" fullWidth>
              上傳手臂配置點（選填）
              <input
                type="file"
                accept={SPEC_INPUT_ACCEPT}
                hidden
                onChange={handleSpecInputChange(setPointListFile)}
              />
            </Button>
            {pointListFile && (
              <Typography variant="body2" color="success.main" sx={{ pl: 1 }}>
                ✓ {pointListFile.name}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button variant="outlined" component="label" fullWidth>
              上傳 IO 點資料表（選填）
              <input
                type="file"
                accept={SPEC_INPUT_ACCEPT}
                hidden
                onChange={handleSpecInputChange(setIoInputFile)}
              />
            </Button>
            {ioInputFile && (
              <Typography variant="body2" color="success.main" sx={{ pl: 1 }}>
                ✓ {ioInputFile.name}
              </Typography>
            )}
          </Box>
        </Box>

        {/* 0226新增 語音上傳 */}
        {/* 語音上傳（展開 User / Engineer） */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setShowAudioUpload((v) => !v)}
          >
            上傳語音（User / Engineer）
          </Button>

          <Collapse in={showAudioUpload}>
            <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
              {/* User 多檔 */}
              <Button variant="outlined" component="label" fullWidth>
                上傳 User 語音
                <input
                  ref={userAudioRef}
                  type="file"
                  multiple
                  accept="audio/*,.mp3,.wav,.m4a,.webm,.ogg"
                  hidden
                  onChange={handleUserAudioChange}
                />
              </Button>

              {/* Engineer 多檔 */}
              <Button variant="outlined" component="label" fullWidth>
                上傳 Engineer
                <input
                  ref={engineerAudioRef}
                  type="file"
                  multiple
                  accept="audio/*,.mp3,.wav,.m4a,.webm,.ogg"
                  hidden
                  onChange={handleEngineerAudioChange}
                />
              </Button>
            </Box>

            {/* 顯示檔名清單 */}
            {newProject.userAudioFiles?.length > 0 && (
              <Box sx={{ pt: 1, pl: 1 }}>
                <Typography variant="body2" color="success.main">
                  ✓ User：
                </Typography>
                {newProject.userAudioFiles.map((f) => (
                  <Typography key={f.name} variant="body2" sx={{ pl: 2 }}>
                    - {f.name}
                  </Typography>
                ))}
              </Box>
            )}

            {newProject.engineerAudioFiles?.length > 0 && (
              <Box sx={{ pt: 1, pl: 1 }}>
                <Typography variant="body2" color="success.main">
                  ✓ Engineer：
                </Typography>
                {newProject.engineerAudioFiles.map((f) => (
                  <Typography key={f.name} variant="body2" sx={{ pl: 2 }}>
                    - {f.name}
                  </Typography>
                ))}
              </Box>
            )}
          </Collapse>
        </Box>
        {/* 0226新增 語音上傳 */}

        {/* 建立按鈕 */}
        <Button
          variant="contained"
          size="medium"
          fullWidth
          loading={isLoading}
          onClick={handleCreateProject}
        >
          建立專案
        </Button>
      </CardContent>
    </Card>
  );
}
